// markdown.ts — a deliberately tiny, XSS-safe markdown SUBSET for grounded
// answers. Pure module (no custom-element registration) so it's unit-testable in
// a DOM-less env and reusable across bubble renderers.
//
// XSS model: every dynamic run of text is INTERPOLATED into a Lit template
// (`${value}`), never concatenated into the static HTML. Lit auto-escapes
// interpolated values at render, so LLM output — including fenced-code contents
// and inline spans — can never inject markup. There is no `unsafeHTML` anywhere.

import { html, type TemplateResult } from 'lit';

/** dedentCode strips a fenced block's COMMON leading indentation and its
 *  surrounding blank lines.
 *
 *  Why it has to exist: the model routinely puts a fence INSIDE a list item, so
 *  every content line arrives carrying the list's indentation, and
 *  `white-space: pre` faithfully preserves it — the code then sits far from the
 *  block's left edge with a wide empty gutter. Observed live on production,
 *  same question twice: 5 spaces and 3 spaces. Every real markdown renderer
 *  dedents; ours did not.
 *
 *  Blank lines do not count towards the measurement (an empty line inside a
 *  block would otherwise force the minimum to zero) and are emitted as truly
 *  empty, so a shorter blank line can never be sliced into negative width. */
export function dedentCode(lines: string[]): string[] {
  let start = 0;
  let end = lines.length;
  while (start < end && lines[start]!.trim() === '') start++;
  while (end > start && lines[end - 1]!.trim() === '') end--;
  const body = lines.slice(start, end);

  // The common STRING prefix, not the shortest length: measuring in characters
  // treats a tab and a space as interchangeable, so a block mixing them would be
  // re-indented into something different from what the model wrote — and that
  // string is what the copy button puts on the clipboard. Whitespace-significant
  // targets (a Makefile recipe) break on exactly that. Same semantics as
  // Python's textwrap.dedent.
  let prefix: string | null = null;
  for (const l of body) {
    if (l.trim() === '') continue;
    const ws = l.slice(0, l.length - l.trimStart().length);
    if (prefix === null) {
      prefix = ws;
      continue;
    }
    let i = 0;
    while (i < prefix.length && i < ws.length && prefix[i] === ws[i]) i++;
    prefix = prefix.slice(0, i);
  }
  if (!prefix) return body;
  const cut = prefix.length;
  return body.map((l) => (l.trim() === '' ? '' : l.slice(cut)));
}

/** Click handler for a code block's copy affordance. The button reports success
 *  through its own dataset (CSS swaps the icon) rather than component state —
 *  renderMarkdown is a pure function and must stay one.
 *
 *  A rejected clipboard (insecure origin, denied permission) leaves the button
 *  untouched: claiming a copy that did not happen is worse than no feedback. */
function clipboardAvailable(): boolean {
  // The Clipboard API exists only in a SECURE context. Everywhere the widget
  // actually ships it is there (https, and http://localhost counts) — but an
  // affordance that is visibly present and silently dead is worse than none.
  return typeof navigator !== 'undefined' && !!navigator.clipboard;
}

function copyCodeHandler(text: string) {
  return (e: Event) => {
    const btn = e.currentTarget as HTMLButtonElement | null;
    if (!btn) return;
    void navigator.clipboard?.writeText(text).then(
      () => {
        btn.dataset.copied = '1';
        setTimeout(() => delete btn.dataset.copied, 1500);
      },
      () => {},
    );
  };
}

/** Inline markdown for a single line: **bold**, *italic*, and `code`. Returns a
 *  mix of plain strings (Lit escapes them) and Lit templates (whose captured
 *  group is itself interpolated → escaped). **bold** is matched before *italic*
 *  so `**x**` isn't read as two italics. Unrecognised text is left verbatim. */
export function renderInline(s: string): Array<TemplateResult | string> {
  const out: Array<TemplateResult | string> = [];
  const re = /\*\*([^*]+)\*\*|\*([^*\n]+)\*|`([^`]+)`/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) out.push(s.slice(last, m.index));
    if (m[1] !== undefined) out.push(html`<strong>${m[1]}</strong>`);
    else if (m[2] !== undefined) out.push(html`<em>${m[2]}</em>`);
    else out.push(html`<code>${m[3]}</code>`);
    last = re.lastIndex;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
}

/** renderMarkdown — paragraphs, "-"/"*" bullet lists, #/##/### headings,
 *  **bold**, *italic*, `code`, and ``` fenced code blocks. Builds Lit templates
 *  ONLY (no unsafeHTML), so every text run is interpolated and auto-escaped.
 *  Not a full parser — enough to stop raw markdown showing in the chat.
 *  Empty/whitespace → the raw string. */
export function renderMarkdown(src: string): TemplateResult | string {
  const text = src ?? '';
  if (!text.trim()) return text;
  const lines = text.split('\n');
  const blocks: TemplateResult[] = [];
  let listItems: TemplateResult[] = [];
  const flushList = () => {
    if (listItems.length) {
      blocks.push(
        html`<ul class="md-list">
          ${listItems}
        </ul>`,
      );
      listItems = [];
    }
  };
  let i = 0;
  while (i < lines.length) {
    const raw = lines[i]!;
    // Fenced code block: ```[lang] … ``` (block-level, spans lines). Contents
    // are joined and interpolated → Lit escapes them (no raw HTML).
    const fence = raw.match(/^\s*```+\s*[\w-]*\s*$/);
    if (fence) {
      flushList();
      const code: string[] = [];
      i++;
      while (i < lines.length && !/^\s*```+\s*$/.test(lines[i]!)) {
        code.push(lines[i]!);
        i++;
      }
      i++; // consume the closing fence (or fall off the end)
      const codeText = dedentCode(code).join('\n');
      // The copy button lives in the WRAPPER, not the <pre>: anchored to the
      // block it must not scroll away when wide content is scrolled sideways.
      const copyBtn = clipboardAvailable()
        ? html`<button
            class="md-copy"
            type="button"
            title="Copy"
            aria-label="Copy code"
            @click=${copyCodeHandler(codeText)}
          >
            <svg class="md-copy-idle" viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
              <rect
                x="5.5"
                y="5.5"
                width="8"
                height="9"
                rx="1.5"
                fill="none"
                stroke="currentColor"
              />
              <path d="M10.5 3.5H3.5a1 1 0 0 0-1 1v7" fill="none" stroke="currentColor" />
            </svg>
            <svg class="md-copy-done" viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
              <path d="M3.5 8.5l3 3 6-6.5" fill="none" stroke="currentColor" stroke-width="1.6" />
            </svg>
          </button>`
        : '';
      blocks.push(
        html`<div class="md-code">
          ${copyBtn}
          <pre class="md-pre"><code>${codeText}</code></pre>
        </div>`,
      );
      continue;
    }
    const line = raw.trimEnd();
    // Heading: #..###### → compact, bubble-friendly heading (level capped at 4
    // for styling). Must start the line (inline "#5" in prose is untouched).
    const heading = line.match(/^\s*(#{1,6})\s+(.*)$/);
    if (heading) {
      flushList();
      const level = Math.min(heading[1]!.length, 4);
      blocks.push(html`<p class="md-h md-h${level}">${renderInline(heading[2]!)}</p>`);
      i++;
      continue;
    }
    const bullet = line.match(/^\s*[-*]\s+(.*)$/);
    if (bullet) {
      listItems.push(html`<li>${renderInline(bullet[1]!)}</li>`);
      i++;
      continue;
    }
    flushList();
    if (line.trim() === '') {
      i++;
      continue;
    }
    blocks.push(html`<p class="md-p">${renderInline(line)}</p>`);
    i++;
  }
  flushList();
  return html`${blocks}`;
}
